<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { BASE_URL } from '@/libs/api-helper';
import { getNetworkInitials } from '@/libs/network-initials';

const props = defineProps<{
  name: string;
  color?: string | null;
  logoUrl?: string;
}>();

// A logo that fails to load gives way to the chip, never to a broken image.
const hasLogoFailed = ref(false);
watch(
  () => props.logoUrl,
  () => (hasLogoFailed.value = false),
);

const showLogo = computed<boolean>(() => Boolean(props.logoUrl) && !hasLogoFailed.value);
</script>

<template>
  <span class="network-badge">
    <img
      v-if="showLogo"
      alt=""
      class="network-badge__logo"
      loading="lazy"
      :src="BASE_URL + logoUrl"
      @error="hasLogoFailed = true"
    />
    <span v-else class="network-badge__chip" :style="{ backgroundColor: color || '#00B871' }">
      {{ getNetworkInitials(name) }}
    </span>
  </span>
</template>

<style lang="scss">
.network-badge {
  display: inline-flex;
  flex-shrink: 0;
  // Height drives the badge: a logo keeps its own ratio, the chip is square.
  height: var(--network-badge-height, 40px);

  &__logo {
    border-radius: $radius-card;
    height: 100%;
    max-width: calc(var(--network-badge-height, 40px) * 2.5);
    object-fit: contain;
    width: auto;
  }

  &__chip {
    align-items: center;
    border-radius: $radius-card;
    color: $color-white;
    display: inline-flex;
    font: $label-medium;
    height: 100%;
    justify-content: center;
    letter-spacing: $label-medium-spacing;
    width: var(--network-badge-height, 40px);
  }
}
</style>
